/**
 * LivePPT H5 视口宽度分档，在移动产品形态内为宽屏和动态窗口发布稳定的 HTML 属性
 */

type ViewportWidthBand =
  | "default"
  | "medium-wide"
  | "large-wide"
  | "extra-wide"
  | "max-wide";

const VIEWPORT_WIDTH_BREAKPOINTS = {
  mediumWide: 600,
  largeWide: 760,
  extraWide: 920,
  maxWide: 1080,
} as const;

let initialized = false;
let resizeObserver: ResizeObserver | undefined;
let resizeFrame = 0;

const resolveViewportWidthBand = (width: number): ViewportWidthBand => {
  if (width >= VIEWPORT_WIDTH_BREAKPOINTS.maxWide) return "max-wide";
  if (width >= VIEWPORT_WIDTH_BREAKPOINTS.extraWide) return "extra-wide";
  if (width >= VIEWPORT_WIDTH_BREAKPOINTS.largeWide) return "large-wide";
  if (width >= VIEWPORT_WIDTH_BREAKPOINTS.mediumWide) return "medium-wide";
  return "default";
};

/**
 * 初始化并持续同步移动工作台的五档视口宽度属性
 *
 * @returns 无返回值；当前档位发布在 html 的 data-layout-tier 与 data-viewport-width-band 属性
 * @sideEffect 浏览器环境会监听根元素尺寸变化
 */
export function initViewportWidthBands(): void {
  if (
    initialized ||
    typeof window === "undefined" ||
    typeof document === "undefined"
  ) {
    return;
  }

  initialized = true;
  const root = document.documentElement;
  const synchronize = () => {
    const band = resolveViewportWidthBand(root.clientWidth || window.innerWidth);
    root.dataset.layoutTier = band;
    root.dataset.viewportWidthBand = band;
  };
  const scheduleSynchronize = () => {
    if (resizeFrame) return;
    resizeFrame = window.requestAnimationFrame(() => {
      resizeFrame = 0;
      synchronize();
    });
  };

  synchronize();
  if (typeof ResizeObserver === "function") {
    resizeObserver = new ResizeObserver(scheduleSynchronize);
    resizeObserver.observe(root);
    return;
  }
  window.addEventListener("resize", scheduleSynchronize, { passive: true });
}
